/**
 * AndroidAppLinksPage — manage Android Digital Asset Links (assetlinks.json)
 * entries. Mirrors BE route `apps/api/src/routes/android-app-links.ts`.
 *
 * UI:
 *  - Header (brand-green gradient headline, no sky accent).
 *  - Create form: host, packageName, fingerprints (multi-line, one per line),
 *    optional label.
 *  - Optional host filter on the list.
 *  - Table of rows with status pill (pending / verified / failed), enable
 *    toggle, and delete action.
 *
 * Standard zinc chrome. Brand green (emerald) only — no sky, cyan, blue, etc.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useAndroidAppLinksList,
	useAndroidAppLinksCreate,
	useAndroidAppLinksDelete,
	useAndroidAppLinksPatch,
} from "./hooks";
import type { AndroidAppLinkView, VerificationStatus } from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const TEXTAREA =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 font-mono text-xs text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";
const BTN_DANGER =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50";
const LABEL =
	"text-xs font-semibold uppercase tracking-wider text-zinc-600";

function statusPill(status: VerificationStatus) {
	const base =
		"inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium leading-none ring-1";
	if (status === "verified")
		return `${base} bg-green-50 text-green-700 ring-green-200`;
	if (status === "failed")
		return `${base} bg-red-50 text-red-700 ring-red-200`;
	return `${base} bg-zinc-100 text-zinc-700 ring-zinc-200`;
}

function parseFingerprintsInput(raw: string): string[] {
	return raw
		.split(/[\n,]+/)
		.map((s) => s.trim())
		.filter((s) => s.length > 0);
}

export function AndroidAppLinksPage() {
	const [hostFilter, setHostFilter] = useState("");
	const [appliedHost, setAppliedHost] = useState<string | undefined>(undefined);

	const listQuery = useAndroidAppLinksList(appliedHost);
	const createMut = useAndroidAppLinksCreate();
	const deleteMut = useAndroidAppLinksDelete();
	const patchMut = useAndroidAppLinksPatch();

	const [host, setHost] = useState("");
	const [packageName, setPackageName] = useState("");
	const [fingerprints, setFingerprints] = useState("");
	const [label, setLabel] = useState("");

	const rows = useMemo(() => listQuery.data?.rows ?? [], [listQuery.data]);

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		const fps = parseFingerprintsInput(fingerprints);
		if (!host.trim() || !packageName.trim() || fps.length === 0) return;
		createMut.mutate(
			{
				host: host.trim(),
				packageName: packageName.trim(),
				fingerprints: fps,
				...(label.trim() ? { label: label.trim() } : {}),
			},
			{
				onSuccess: () => {
					setHost("");
					setPackageName("");
					setFingerprints("");
					setLabel("");
				},
			},
		);
	};

	const handleApplyHostFilter = (e: FormEvent) => {
		e.preventDefault();
		const trimmed = hostFilter.trim();
		setAppliedHost(trimmed ? trimmed : undefined);
	};

	const clearHostFilter = () => {
		setHostFilter("");
		setAppliedHost(undefined);
	};

	return (
		<main
			data-testid="android-app-links-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					android-app-links
				</p>
				<h1 className="text-3xl font-semibold text-green-700">
					Android App Links
				</h1>
				<p className="max-w-2xl text-sm text-zinc-600">
					Publish Digital Asset Links entries so the Android OS resolves
					verified http(s) URLs directly to the installed app. One row per
					(host, package name) pair.
				</p>
			</header>

			<section className={CARD} aria-label="Create Android App Link">
				<h2 className="text-base font-semibold text-zinc-900">Create new</h2>
				<form
					onSubmit={handleCreate}
					className="mt-4 grid gap-4 sm:grid-cols-2"
					data-testid="android-app-links-create-form"
				>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Host</span>
						<input
							type="text"
							className={INPUT}
							placeholder="app.example.com"
							value={host}
							onChange={(e) => setHost(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="android-app-links-host-input"
							autoComplete="off"
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Package name</span>
						<input
							type="text"
							className={INPUT}
							placeholder="com.example.app"
							value={packageName}
							onChange={(e) => setPackageName(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="android-app-links-package-input"
							autoComplete="off"
						/>
					</label>
					<label className="flex flex-col gap-1 sm:col-span-2">
						<span className={LABEL}>
							SHA-256 fingerprints (one per line)
						</span>
						<textarea
							className={TEXTAREA}
							placeholder="AA:BB:CC:..."
							value={fingerprints}
							onChange={(e) => setFingerprints(e.currentTarget.value)}
							disabled={createMut.isPending}
							rows={4}
							data-testid="android-app-links-fingerprints-input"
						/>
					</label>
					<label className="flex flex-col gap-1 sm:col-span-2">
						<span className={LABEL}>Label (optional)</span>
						<input
							type="text"
							className={INPUT}
							placeholder="Production release key"
							value={label}
							onChange={(e) => setLabel(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="android-app-links-label-input"
							autoComplete="off"
						/>
					</label>
					<div className="sm:col-span-2">
						<button
							type="submit"
							className={BTN}
							disabled={
								createMut.isPending ||
								!host.trim() ||
								!packageName.trim() ||
								parseFingerprintsInput(fingerprints).length === 0
							}
							data-testid="android-app-links-submit"
						>
							{createMut.isPending ? "Saving." : "Create"}
						</button>
					</div>
				</form>
				{createMut.error ? (
					<p
						className="mt-3 text-xs text-red-700"
						role="alert"
						data-testid="android-app-links-create-error"
					>
						{createMut.error.message}
					</p>
				) : null}
			</section>

			<section className={CARD} aria-label="Android App Links list">
				<div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
					<h2 className="text-base font-semibold text-zinc-900">Entries</h2>
					<form
						onSubmit={handleApplyHostFilter}
						className="flex items-end gap-2"
						data-testid="android-app-links-filter-form"
					>
						<label className="flex flex-col gap-1">
							<span className={LABEL}>Filter by host</span>
							<input
								type="text"
								className={INPUT + " sm:w-64"}
								placeholder="app.example.com"
								value={hostFilter}
								onChange={(e) => setHostFilter(e.currentTarget.value)}
								data-testid="android-app-links-host-filter-input"
								autoComplete="off"
							/>
						</label>
						<button type="submit" className={BTN_GHOST}>
							Apply
						</button>
						{appliedHost ? (
							<button
								type="button"
								className={BTN_GHOST}
								onClick={clearHostFilter}
								data-testid="android-app-links-clear-filter"
							>
								Clear
							</button>
						) : null}
					</form>
				</div>

				{listQuery.isLoading ? (
					<p
						className="mt-4 text-sm text-zinc-500"
						data-testid="android-app-links-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p
						className="mt-4 text-sm text-red-700"
						role="alert"
						data-testid="android-app-links-list-error"
					>
						{listQuery.error.message}
					</p>
				) : rows.length === 0 ? (
					<p
						className="mt-4 text-sm text-zinc-500"
						data-testid="android-app-links-empty"
					>
						No entries yet.
					</p>
				) : (
					<div
						className="mt-4 overflow-x-auto"
						data-testid="android-app-links-list"
					>
						<table className="min-w-full divide-y divide-zinc-200 text-sm">
							<thead>
								<tr className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
									<th className="px-3 py-2 font-semibold">Host</th>
									<th className="px-3 py-2 font-semibold">Package</th>
									<th className="px-3 py-2 font-semibold">Fingerprints</th>
									<th className="px-3 py-2 font-semibold">Status</th>
									<th className="px-3 py-2 font-semibold">Enabled</th>
									<th className="px-3 py-2 font-semibold text-right">
										Actions
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100">
								{rows.map((row: AndroidAppLinkView) => (
									<tr
										key={row.id}
										data-testid={`android-app-links-row-${row.id}`}
									>
										<td className="px-3 py-2 font-mono text-xs text-zinc-900">
											{row.host}
										</td>
										<td className="px-3 py-2 font-mono text-xs text-zinc-900">
											{row.package_name}
										</td>
										<td className="px-3 py-2 font-mono text-[11px] text-zinc-600">
											{row.fingerprints.length} fp
										</td>
										<td className="px-3 py-2">
											<span className={statusPill(row.verification_status)}>
												{row.verification_status}
											</span>
										</td>
										<td className="px-3 py-2">
											<button
												type="button"
												className={BTN_GHOST}
												onClick={() =>
													patchMut.mutate({
														id: row.id,
														body: { enabled: !row.enabled },
													})
												}
												disabled={patchMut.isPending}
												data-testid={`android-app-links-toggle-${row.id}`}
											>
												{row.enabled ? "Enabled" : "Disabled"}
											</button>
										</td>
										<td className="px-3 py-2 text-right">
											<button
												type="button"
												className={BTN_DANGER}
												onClick={() => deleteMut.mutate(row.id)}
												disabled={deleteMut.isPending}
												data-testid={`android-app-links-delete-${row.id}`}
											>
												Delete
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</main>
	);
}

export default AndroidAppLinksPage;
